/**
 * finance-core — Đối soát số dư ví với lịch sử (GĐ2). Thuần hàm; chỉ BÁO, không bao giờ sửa.
 *
 * Số dư kỳ vọng = tổng các dòng lịch sử `transactions` của ví, cộng các luồng KHÔNG
 * ghi dòng lịch sử (trả thẻ, trả nợ):
 *   ví thường: − trả thẻ từ ví − trả nợ từ ví          thẻ: − tiền đã trả cho thẻ
 * Điểm xuất phát: `initialBalance` của ví (nếu có) — trừ khi đã có mốc điều chỉnh.
 * (`charge` của thẻ là số DƯƠNG: tăng dư nợ.)
 *
 * MỐC ĐIỀU CHỈNH: "Điều chỉnh số dư" ghi dòng `adjustment` có `newValue`. Từ lần điều chỉnh
 * gần nhất, kỳ vọng = newValue + các luồng SAU mốc — nhờ đó một lần đối soát thật sự khép
 * khoảng lệch cũ, thay vì khoảng lệch dai dẳng mãi (biến động cũ không truy được).
 */

export interface ReconcileSource { id: string; name?: string; type?: string; balance?: number; usedCredit?: number; initialBalance?: number | null }
export interface TxLogLike { sourceId?: string; amount?: number; type?: string; newValue?: number; timestamp?: any; date?: any; createdAt?: any }
export interface CardPaymentLike { creditCardId?: string; sourceWalletId?: string; amount?: number; date?: any }
export interface DebtPaymentLike { sourceId?: string | null; totalAmount?: number; amount?: number; paymentDate?: any }

export interface ReconcileRow {
  id: string; name: string; type: string;
  stored: number; expected: number; diff: number;
  matched: boolean;
  /** Đã có lần điều chỉnh làm mốc */
  anchored: boolean;
}

const when = (v: any): number | null => {
  if (!v) return null;
  const d = typeof v.toDate === 'function' ? v.toDate() : new Date(v);
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
};

export const reconcileWallets = (input: {
  sources: ReconcileSource[];
  txLogs: TxLogLike[];
  creditPayments: CardPaymentLike[];
  debtPayments?: DebtPaymentLike[];
}): ReconcileRow[] => {
  const { sources, txLogs, creditPayments, debtPayments = [] } = input;
  return sources.map(s => {
    const credit = s.type === 'credit';
    const events: Array<{ t: number | null; delta: number }> = [];
    let anchor: { t: number; value: number } | null = null;

    for (const l of txLogs) {
      if (l.sourceId !== s.id) continue;
      const t = when(l.timestamp) ?? when(l.date) ?? when(l.createdAt);
      if (l.type === 'adjustment' && typeof l.newValue === 'number') {
        const at = t ?? 0;
        if (!anchor || at >= anchor.t) anchor = { t: at, value: l.newValue };
      } else {
        events.push({ t, delta: Number(l.amount) || 0 });
      }
    }
    for (const p of creditPayments) {
      const amt = Number(p.amount) || 0;
      if (credit ? p.creditCardId === s.id : p.sourceWalletId === s.id) events.push({ t: when(p.date), delta: -amt });
    }
    if (!credit) {
      for (const p of debtPayments) {
        if (p.sourceId === s.id) events.push({ t: when(p.paymentDate), delta: -(Number(p.totalAmount ?? p.amount) || 0) });
      }
    }

    const after = anchor ? events.filter(e => e.t !== null && e.t > anchor!.t) : events;
    // Số dư đầu kỳ (field `initialBalance` lúc tạo ví) là điểm xuất phát — chỉ khi chưa có mốc điều chỉnh
    const start = anchor ? anchor.value : (credit ? 0 : Number(s.initialBalance) || 0);
    const expected = start + after.reduce((sum, e) => sum + e.delta, 0);
    const stored = credit ? Number(s.usedCredit) || 0 : Number(s.balance) || 0;
    const diff = stored - expected;
    return {
      id: s.id, name: s.name ?? '', type: s.type ?? '',
      stored, expected, diff, matched: Math.abs(diff) < 1, anchored: !!anchor
    };
  });
};
